import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import styles from './A_GlassTooltip.module.css';

interface A_GlassTooltipProps {
  content: string;
  children: React.ReactElement;
}

export default function A_GlassTooltip({
  content,
  children,
}: A_GlassTooltipProps) {
  const tooltipId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const show = hover || focus || pinned;

  const updatePosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 8;
    const centerX = r.left + r.width / 2;
    const estHeight = 120;
    let top = r.bottom + gap;

    if (top + estHeight > window.innerHeight - 12) {
      top = Math.max(12, r.top - gap - estHeight);
    }

    setCoords({ left: centerX, top });
  }, []);

  useLayoutEffect(() => {
    if (!show) return;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [show, updatePosition]);

  useEffect(() => {
    if (!pinned) return;
    const close = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setPinned(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [pinned]);

  const handlePointerEnter = () => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(hover: none)').matches) return;
    setHover(true);
  };

  const handlePointerLeave = () => {
    setHover(false);
  };

  const handleWrapClick = (e: React.MouseEvent) => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(hover: hover)').matches) return;
    e.stopPropagation();
    setPinned(p => !p);
  };

  const trigger = React.cloneElement(children, {
    'aria-describedby': show ? tooltipId : undefined,
  });

  const tooltipNode =
    show && typeof document !== 'undefined' ? (
      <div
        id={tooltipId}
        className={cn(styles.tooltip, 'effect-glass')}
        style={{
          left: coords.left,
          top: coords.top,
          transform: 'translateX(-50%)',
        }}
        role='tooltip'
      >
        <p className={cn(styles.text, 'text-caption')}>{content}</p>
      </div>
    ) : null;

  return (
    <>
      <div
        ref={wrapRef}
        className={styles.trigger_wrap}
        onMouseEnter={handlePointerEnter}
        onMouseLeave={handlePointerLeave}
        onFocusCapture={() => setFocus(true)}
        onBlurCapture={() => {
          setFocus(false);
          setPinned(false);
        }}
        onClick={handleWrapClick}
      >
        {trigger}
      </div>
      {tooltipNode ? createPortal(tooltipNode, document.body) : null}
    </>
  );
}
